import { ApiError, api, call } from "../api/client";
import { markAttempt, markFailed, markSent, nextPending } from "./store";
import type { QueuedOp } from "./types";

/** What to do after an attempt: done, try again later, give up on it, or sign in again. */
export type Outcome = "ok" | "retry" | "fatal" | "auth";

export function classify(e: unknown): Outcome {
  if (!(e instanceof ApiError)) return "retry"; // Unexpected; keep it and try again.
  if (e.status === 0) return "retry"; // No connection or timed out.
  if (e.status === 401) return "auth";
  if (e.status === 408 || e.status === 429 || e.status >= 500) return "retry";
  return "fatal"; // The server understood and refused (e.g. 422): retrying won't help.
}

/** Send one change. Every kind is safe to send more than once. */
export async function sendOp(op: QueuedOp): Promise<void> {
  switch (op.kind) {
    case "putEntry":
      await call(
        api.PUT("/api/v1/entries/{entry_id}", { params: { path: { entry_id: op.id } }, body: op.body }),
      );
      return;
    case "deleteEntry":
      await call(api.DELETE("/api/v1/entries/{entry_id}", { params: { path: { entry_id: op.id } } }));
      return;
    case "increment":
      await call(
        api.POST("/api/v1/metrics/increment", {
          params: { header: { "Idempotency-Key": op.key } },
          body: op.body,
        }),
      );
      return;
    case "setMetric":
      if (op.value == null) {
        await call(
          api.DELETE("/api/v1/metrics", { params: { query: { date: op.date, metric: op.metric, source: "manual" } } }),
        );
      } else {
        await call(
          api.PUT("/api/v1/metrics", {
            body: [{ date: op.date, metric: op.metric, value: op.value, source: "manual" }],
          }),
        );
      }
      return;
    case "createFoodAndLog": {
      // If this stops halfway, the retry gets the same food back (same key) and logs it.
      const food = await call(
        api.POST("/api/v1/foods", { params: { header: { "Idempotency-Key": op.foodKey } }, body: op.food }),
      );
      await call(
        api.PUT("/api/v1/entries/{entry_id}", {
          params: { path: { entry_id: op.entryId } },
          body: { ...op.entry, food_id: food.id },
        }),
      );
      return;
    }
  }
}

// Only one sender at a time, across tabs (Web Locks) or within this tab (a promise chain).
let localChain: Promise<unknown> = Promise.resolve();
function withLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && navigator.locks) {
    return navigator.locks.request("ct-sync-outbox", fn) as Promise<T>;
  }
  const run = localChain.then(fn, fn);
  localChain = run.catch(() => undefined);
  return run;
}

export type FlushResult = { sent: QueuedOp[]; stopped: "retry" | "auth" | null; error?: string };

/** Send this user's pending changes, oldest first, until done or the connection fails. */
export function flush(userId: number, send: (op: QueuedOp) => Promise<void> = sendOp): Promise<FlushResult> {
  return withLock(async () => {
    const sent: QueuedOp[] = [];
    for (;;) {
      const op = await nextPending(userId);
      if (!op) return { sent, stopped: null };
      try {
        await send(op);
      } catch (e) {
        const outcome = classify(e);
        const message = e instanceof Error ? e.message : String(e);
        if (outcome === "fatal") {
          await markFailed(op, message);
          continue;
        }
        await markAttempt(op, message);
        return { sent, stopped: outcome === "auth" ? "auth" : "retry", error: message };
      }
      await markSent(op);
      sent.push(op);
    }
  });
}
